import { Fragment, useContext, useEffect, useState } from 'react'
import { AnimatePresence, m, Variants } from 'framer-motion'
import Cookies from 'js-cookie'

import { SanityCookieConsent } from '@data/sanity/queries/types/site'
import { useHasMounted } from '@lib/helpers'
import { StringsContext } from '@lib/strings'

import Button, { ButtonVariant } from '@components/buttons/button'
import CustomLink from '@components/link'

interface AcceptCookies {
  acceptedCookies: boolean
  onAcceptCookies: () => void
}

type CookieBarProps = SanityCookieConsent

const barAnimation: Variants = {
  show: {
    y: '0%',
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1],
    },
  },
  hide: {
    y: '100%',
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1],
    },
  },
}

const useAcceptCookies = (cookieName = 'accept_cookies'): AcceptCookies => {
  const [acceptedCookies, setAcceptedCookies] = useState(true)

  useEffect(() => {
    if (!Cookies.get(cookieName)) {
      setAcceptedCookies(false)
    }
  }, [cookieName])

  return {
    acceptedCookies,
    onAcceptCookies: () => {
      setAcceptedCookies(true)
      Cookies.set(cookieName, 'accepted', { expires: 365 })
    },
  }
}

const CookieBar = ({ enabled, message, link }: CookieBarProps) => {
  const strings = useContext(StringsContext)

  const hasMounted = useHasMounted()
  const { acceptedCookies, onAcceptCookies } = useAcceptCookies()

  if (!enabled || !hasMounted || !message || acceptedCookies) {
    return null
  }

  return (
    <AnimatePresence>
      <m.div
        initial="show"
        animate="show"
        exit="hide"
        variants={barAnimation}
        role="dialog"
        aria-live="polite"
        className="fixed bottom-0 right-0 z-90 p-4 w-full max-w-3xl"
      >
        <div className="grid gap-6 sm:flex items-center p-6 pb-4 sm:p-6 rounded-lg bg-pageText text-pageBG">
          <div className="flex-1 sm:pr-8 text-center sm:text-left">
            <p className="text-sm leading-normal">
              {message.split('\n').map((text, index) => (
                <Fragment key={index}>
                  {text}
                  {message.split('\n')[index + 1] && <br />}
                </Fragment>
              ))}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-1 flex-shrink-0">
            {link && (
              <CustomLink link={{ page: link }} className="py-4">
                {strings.buttonLearnMore}
              </CustomLink>
            )}

            <Button
              onClick={() => onAcceptCookies()}
              variant={ButtonVariant.PRIMARY}
            >
              {strings.buttonAccept}
            </Button>
          </div>
        </div>
      </m.div>
    </AnimatePresence>
  )
}

export default CookieBar
